import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useParams } from "react-router";
import { observer } from "mobx-react-lite";
import { IconCamera, IconCheck, IconRefresh, IconUpload, IconX, IconAlertCircle } from "@tabler/icons-react";

import { useStore } from "../../../helpers";
import { base64ToBlob, blobToFile, useFileUpload } from "../../../helpers";
import { extractFaceMatchServiceRequests } from "./face-match-utils";
import { FaceSearchError, parseFaceSearchError } from "./face-search-errors";
import FaceSearchFeedback from "./face-search-feedback";

type ServiceRequestFaceRecognitionProps = {
    onResults?: (results: any[]) => void;
    onSelectRequest?: (requests: any[]) => void;
};

const ServiceRequestFaceRecognition = ({
    onResults,
    onSelectRequest,
}: ServiceRequestFaceRecognitionProps) => {
    /*
     * ============================================================
     * REFS
     * ============================================================
     */

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    /*
     * ============================================================
     * STORE
     * ============================================================
     */

    const { projectid } = useParams();
    const rootStore = useStore();

    const { uploadFileToS3 } = useFileUpload();

    /*
     * ============================================================
     * STATE
     * ============================================================
     */

    const [isOpen, setIsOpen] = useState(false);

    const [capturedImage, setCapturedImage] = useState<string | null>(null);

    const [isSearching, setIsSearching] = useState(false);

    const [cameraAllowed, setCameraAllowed] = useState(true);

    const [cameraLoading, setCameraLoading] = useState(false);

    const [results, setResults] = useState<any[]>([]);

    const [showResults, setShowResults] = useState(false);

    const [faceError, setFaceError] = useState<FaceSearchError | null>(null);

    /*
     * ============================================================
     * START CAMERA
     * ============================================================
     */

    const startCamera = async () => {
        try {
            setCameraLoading(true);
            setCameraAllowed(true);

            if (!navigator.mediaDevices?.getUserMedia) {
                setCameraAllowed(false);
                return;
            }

            // Stop any previous camera stream
            stopCamera();

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: "user",
                    width: {
                        ideal: 1280,
                    },
                    height: {
                        ideal: 800,
                    },
                },
                audio: false,
            });

            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;

                await videoRef.current.play();
            }
        } catch (error) {
            console.error("Unable to access camera:", error);

            setCameraAllowed(false);
        } finally {
            setCameraLoading(false);
        }
    };

    /*
     * ============================================================
     * STOP CAMERA
     * ============================================================
     */

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => {
                track.stop();
            });

            streamRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    /*
     * ============================================================
     * OPEN CAMERA
     * ============================================================
     */

    const openCamera = () => {
        setCapturedImage(null);
        setResults([]);
        setShowResults(false);
        setFaceError(null);
        setCameraAllowed(true);

        setIsOpen(true);
    };

    /*
     * ============================================================
     * CAMERA LIFECYCLE
     * ============================================================
     */

    useEffect(() => {
        if (!isOpen) {
            stopCamera();
            return;
        }

        if (!capturedImage && !isSearching && !faceError) {
            startCamera();
        }

        return () => {
            stopCamera();
        };
    }, [isOpen, capturedImage, isSearching, faceError]);

    /*
     * ============================================================
     * CLOSE CAMERA
     * ============================================================
     */

    const closeModal = () => {
        if (isSearching) {
            return;
        }

        stopCamera();

        setIsOpen(false);
        setCapturedImage(null);
        setCameraAllowed(true);
        setCameraLoading(false);
        setFaceError(null);
        setResults([]);
        setShowResults(false);
    };

    /*
     * ============================================================
     * CAPTURE IMAGE
     * ============================================================
     */

    const handleCapture = () => {
        try {
            const video = videoRef.current;

            if (!video) {
                toast.error("Camera is not available.");
                return;
            }

            if (
                video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
                video.videoWidth === 0 ||
                video.videoHeight === 0
            ) {
                toast.error("Camera is not ready yet.");
                return;
            }

            const canvas = document.createElement("canvas");

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const context = canvas.getContext("2d");

            if (!context) {
                toast.error("Unable to capture image.");
                return;
            }

            /*
             * Mirror the image because front camera preview
             * is normally mirrored.
             */
            context.translate(canvas.width, 0);
            context.scale(-1, 1);

            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            const image = canvas.toDataURL("image/jpeg", 0.9);

            setCapturedImage(image);

            stopCamera();
        } catch (error: any) {
            console.error("Unable to capture face:", error);

            toast.error(error?.message || "Unable to capture image.");
        }
    };

    /*
     * ============================================================
     * RETAKE
     * ============================================================
     */

    const handleRetake = () => {
        setCapturedImage(null);
        setFaceError(null);
        setResults([]);
        setShowResults(false);
        setCameraAllowed(true);
    };

    /*
     * ============================================================
     * UPLOAD IMAGE FROM DEVICE
     * ============================================================
     */

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        if (!file.type.startsWith("image/")) {
            toast.error("Please select an image file.");
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {
            if (typeof reader.result === "string") {
                stopCamera();
                setCapturedImage(reader.result);
            }
        };

        reader.onerror = () => {
            toast.error("Unable to read the selected image.");
        };

        reader.readAsDataURL(file);

        // Allow selecting the same image again
        event.target.value = "";
    };

    /*
     * ============================================================
     * FETCH SERVICE REQUESTS BY FACE
     * ============================================================
     */

    const handleFaceSearch = async () => {
        if (!capturedImage) {
            toast.error("Please capture or upload an image first.");
            return;
        }

        if (!projectid) {
            toast.error("Project ID is missing.");
            return;
        }

        try {
            setIsSearching(true);
            setFaceError(null);
            setResults([]);
            setShowResults(false);

            /*
             * ----------------------------------------------------
             * 1. Convert captured image to Blob
             * ----------------------------------------------------
             */

            const blob = base64ToBlob(capturedImage);

            /*
             * ----------------------------------------------------
             * 2. Create file
             * ----------------------------------------------------
             */

            const file = blobToFile(blob, `service_request_face_${Date.now()}.jpeg`);

            /*
             * ----------------------------------------------------
             * 3. Upload image to S3
             * ----------------------------------------------------
             */

            const uploadResult = await uploadFileToS3(`sr_profile_photos/find`, file);

            const s3Link = uploadResult?.key;

            if (!s3Link) {
                setFaceError({
                    code: "IMAGE_UNREADABLE",
                    message: "Unable to upload face image.",
                });
                return;
            }

            console.log("FACE IMAGE S3 LINK:", s3Link);

            /*
             * ----------------------------------------------------
             * 4. Hit Service Request Face API
             * ----------------------------------------------------
             */

            const response = await rootStore.api.post("unified_service_request/sr-by-face", {
                projectid: Number(projectid),
                s3_link: s3Link,
                store_photo: true,
            });

            console.log("SERVICE REQUEST FACE RESPONSE:", response);

            /*
             * ----------------------------------------------------
             * 5. Handle structured success / error responses
             * ----------------------------------------------------
             */

            if (response?.status !== 200) {
                setFaceError(parseFaceSearchError(response));
                return;
            }

            const responseData: any = response;

            const matchedVoters = Array.isArray(responseData?.data?.result)
                ? responseData.data.result
                : Array.isArray(responseData?.data?.payload)
                  ? responseData.data.payload
                  : Array.isArray(responseData?.data)
                    ? responseData.data
                    : Array.isArray(responseData?.result)
                      ? responseData.result
                      : Array.isArray(responseData?.payload)
                        ? responseData.payload
                        : [];

            const matchedRequests = extractFaceMatchServiceRequests(matchedVoters);

            console.log("MATCHED VOTERS:", matchedVoters);
            console.log("MATCHED SERVICE REQUESTS:", matchedRequests);

            if (onResults) {
                onResults(matchedRequests);
            }

            if (matchedRequests.length === 0) {
                setFaceError({
                    code: "NO_FACE_MATCH",
                    message: "No service requests found for this face.",
                });
                return;
            }

            setResults(matchedRequests);
            setShowResults(true);

            toast.success(
                `${matchedRequests.length} service request${matchedRequests.length > 1 ? "s" : ""} found.`
            );
        } catch (error: any) {
            console.error("Service request face recognition failed:", error);

            if (error?.data?.code || error?.response?.data?.code) {
                setFaceError(parseFaceSearchError(error?.response || error));
            } else {
                setFaceError({
                    code: "FACE_SEARCH_FAILED",
                    message: "Unable to fetch service requests by face.",
                });
            }
        } finally {
            setIsSearching(false);
        }
    };

    /*
     * ============================================================
     * RENDER
     * ============================================================
     */

    return (
        <>
            {/* =====================================================
                FACIAL RECOGNITION BUTTON
            ====================================================== */}

            <button
                type="button"
                onClick={openCamera}
                title="Fetch Service Requests by Face"
                aria-label="Fetch Service Requests by Face"
                className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-500 text-white shadow-sm transition hover:bg-blue-600"
            >
                <IconCamera size={20} />
            </button>

            {/* =====================================================
                CAMERA MODAL
            ====================================================== */}

            {isOpen && (
                <div
                    className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            closeModal();
                        }
                    }}
                >
                    <div
                        className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        {/* =================================================
                            HEADER
                        ================================================== */}

                        <div className="flex items-center justify-between border-b px-5 py-4">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Fetch Service Requests
                                </h3>

                                <p className="mt-1 text-sm text-gray-500">
                                    Capture the voter's face to find their service requests.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={closeModal}
                                disabled={isSearching}
                                className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 disabled:opacity-50"
                            >
                                <IconX size={20} />
                            </button>
                        </div>

                        {/* =================================================
                            CONTENT
                        ================================================== */}

                        <div className="p-5">
                            {isSearching || faceError ? (
                                <FaceSearchFeedback
                                    isSearching={isSearching}
                                    error={faceError}
                                    onRetry={handleRetake}
                                    onClose={closeModal}
                                />
                            ) : !capturedImage ? (
                                <>
                                    <div className="relative mx-auto aspect-[4/3] w-full max-w-xl overflow-hidden rounded-xl bg-black">
                                        {cameraAllowed ? (
                                            <>
                                                {/* Native browser camera */}

                                                <video
                                                    ref={videoRef}
                                                    autoPlay
                                                    playsInline
                                                    muted
                                                    className="h-full w-full object-cover"
                                                />

                                                {/* Loading overlay */}

                                                {cameraLoading && (
                                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                                        <div className="text-sm font-medium text-white">
                                                            Starting camera...
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Face scanning frame */}

                                                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                                    <div className="h-[65%] w-[55%] rounded-[45%] border-2 border-white/70" />
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex h-full min-h-[280px] flex-col items-center justify-center bg-red-50 px-6 text-center">
                                                <IconAlertCircle size={40} className="mb-3 text-red-600" />

                                                <p className="mb-2 font-medium text-red-700">
                                                    Camera access blocked
                                                </p>

                                                <p className="mb-4 text-sm text-gray-600">
                                                    Please allow camera access or upload an image instead.
                                                </p>

                                                <button
                                                    type="button"
                                                    onClick={startCamera}
                                                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                                                >
                                                    Try Again
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* =================================================
                                        CAMERA ACTIONS
                                    ================================================== */}

                                    <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleCapture}
                                            disabled={cameraLoading || !cameraAllowed}
                                            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <IconCamera size={18} />
                                            Capture
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                                        >
                                            <IconUpload size={18} />
                                            Upload Image
                                        </button>

                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleFileChange}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* =================================================
                                        IMAGE PREVIEW
                                    ================================================== */}

                                    <div className="relative mx-auto aspect-[4/3] w-full max-w-xl overflow-hidden rounded-xl bg-gray-100">
                                        <img
                                            src={capturedImage}
                                            alt="Face preview"
                                            className="h-full w-full object-contain"
                                        />
                                    </div>

                                    {/* =================================================
                                        PREVIEW ACTIONS
                                    ================================================== */}

                                    <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleRetake}
                                            disabled={isSearching}
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            <IconRefresh size={18} />
                                            Retake
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isSearching}
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            <IconUpload size={18} />
                                            Re-upload
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleFaceSearch}
                                            disabled={isSearching}
                                            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <IconCheck size={18} />

                                            {isSearching ? "Searching..." : "Find Requests"}
                                        </button>

                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleFileChange}
                                        />
                                    </div>
                                </>
                            )}

                            {/* =================================================
                                RESULTS
                            ================================================== */}

                            {showResults && !isSearching && !faceError && (
                                <div className="mt-6 border-t pt-5">
                                    {results.length === 0 ? (
                                        <div className="rounded-xl bg-gray-50 p-6 text-center">
                                            <p className="text-sm text-gray-500">
                                                No service requests found.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                                            <div className="flex items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-sm font-semibold text-blue-900">
                                                        {results.length} service request
                                                        {results.length > 1 ? "s" : ""} found
                                                    </p>

                                                    <p className="mt-1 text-xs text-blue-700">
                                                        Click below to view all related requests in the table.
                                                    </p>
                                                </div>

                                                <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
                                                    {results.length}
                                                </span>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onSelectRequest?.(results);
                                                    closeModal();
                                                }}
                                                className="mt-4 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
                                            >
                                                Click to see
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default observer(ServiceRequestFaceRecognition);
